import React from 'react';

import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { cutoffCalculation } from '../services/cutoff-calculation';
import useTimeFormat from '../../../hooks/use-time-format';
import {
	DailyCutoff,
	RelativeCutoff,
	RelativeOptions
} from 'bringg-web/features/planned-delivery-windows/modals/slots-modal/wizard-steps/configuration-step/time-slots/cutoff-radio-group/cutoff.types';

import styles from './cutoff.module.scss';

export enum CutoffMode {
	DAY_AND_TIME = 'DAY_AND_TIME',
	DAILY = 'DAILY',
	RELATIVE = 'RELATIVE'
}

interface Props {
	startWindowTime: Date;
	cutoff: number;
	timezone: string;
	mode?: CutoffMode;
	showText?: boolean;
	labelClassName?: string;
}
export const Cutoff = ({
	startWindowTime,
	cutoff,
	mode = CutoffMode.DAY_AND_TIME,
	showText = true,
	timezone,
	labelClassName
}: Props) => {
	const format = useTimeFormat();
	const { t } = useTranslation();

	const dayAndTimeText = (startTime: Date, cutoffWindow: number): string => {
		const startWindowTimeDate = moment(startTime).tz(timezone).date();
		const cutoffDate = moment(startTime).tz(timezone).subtract(cutoffWindow, 'm').date();

		if (startWindowTimeDate - cutoffDate === 0) {
			const sameDayText = t('PLANNED_DELIVERY_WINDOWS_POPOVER.SAME_DAY');
			const atText = t('PLANNED_DELIVERY_WINDOWS_POPOVER.AT');
			return `${sameDayText}, ${atText} ${cutoffCalculation
				.getDate(startTime, cutoffWindow, timezone)
				.format(format)}`;
		}

		return cutoffCalculation.getDate(startTime, cutoffWindow, timezone).format(`dddd ${format}`);
	};
	const relativeCutoffToText = (relativeCutoff: RelativeCutoff): string => {
		let text = '';
		if (relativeCutoff.days > 0) {
			text += `${relativeCutoff.days}${t('DELIVERY_WINDOW_CUTOFF_COMPONENT.DAYS_FIRST_LETTER')} `;
		}
		if (relativeCutoff.hours > 0) {
			text += `${relativeCutoff.hours}${t('DELIVERY_WINDOW_CUTOFF_COMPONENT.HOUR_FIRST_LETTER')} `;
		}
		if (relativeCutoff.minutes > 0) {
			text += `${relativeCutoff.minutes}${t('DELIVERY_WINDOW_CUTOFF_COMPONENT.MINUTE_FIRST_LETTER')} `;
		}
		if (text && relativeCutoff.relativeOption === RelativeOptions.AFTER) {
			text += `${t('DELIVERY_WINDOW_CUTOFF_COMPONENT.AFTER')}`;
		}
		if (text && relativeCutoff.relativeOption === RelativeOptions.BEFORE) {
			text += `${t('DELIVERY_WINDOW_CUTOFF_COMPONENT.BEFORE')}`;
		}

		return text;
	};

	const dailyCutoffToText = (dailyCutoff: DailyCutoff): string => {
		if (dailyCutoff.days > 0) {
			const daysText = `${dailyCutoff.days} ${t('PLANNED_DELIVERY_WINDOWS_POPOVER.DAYS_BEFORE')}`;
			return `${daysText}, ${t('PLANNED_DELIVERY_WINDOWS_POPOVER.AT')} ${moment(dailyCutoff.time)
				.tz(timezone)
				.format(format)}`;
		}

		return `${t('PLANNED_DELIVERY_WINDOWS_POPOVER.SAME_DAY')} ${moment(dailyCutoff.time)
			.tz(timezone)
			.format(format)}`;
	};

	const getCutoffView = (): string => {
		if (mode === CutoffMode.DAY_AND_TIME) {
			return dayAndTimeText(startWindowTime, cutoff);
		}
		if (mode === CutoffMode.RELATIVE) {
			return relativeCutoffToText(cutoffCalculation.cutoffToRelative(cutoff));
		}
		if (mode === CutoffMode.DAILY) {
			return dailyCutoffToText(cutoffCalculation.cutoffToDaily(cutoff, startWindowTime, timezone));
		}
		return '';
	};

	const cutoffView = getCutoffView();

	return (
		<div className={styles.cutoff}>
			{showText && (
				<span className={`${styles.cutoffTranslate} ${labelClassName ?? ''}`}>{`${t(
					'PLANNED_DELIVERY_WINDOWS.POPOVER.CUTOFF'
				)}: `}</span>
			)}
			<span className={styles.cutoffValue}>{cutoffView || t('PLANNED_DELIVERY_WINDOWS_POPOVER.NO_CUTOFF')}</span>
		</div>
	);
};
